import shouldForwardProp from '@styled-system/should-forward-prop'
import { cn } from '@utils/cn'
import { ScrollTrigger } from '@utils/gsap'
import { Heading } from '@utils/Typography'
import { Box, Hide } from 'mds-web-ui'
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { ChevronUp } from 'react-feather'
import styled from 'styled-components'
import {
  color,
  flexbox,
  layout,
  position,
  space,
  typography
} from 'styled-system'

const getScrollParent = node => {
  if (!node) {
    return null
  }

  let parent = node.parentElement
  while (parent) {
    const { overflowY, overflow } = getComputedStyle(parent)
    if (/(auto|scroll)/.test(`${overflow}${overflowY}`)) {
      return parent
    }
    parent = parent.parentElement
  }

  return null
}

const StyledNavLink = styled(Box).withConfig({ shouldForwardProp })({
  fontSize: '14px',
  color: 'rgb(0 0 0)',
  opacity: 0.4,
  textWrap: 'pretty',
  position: 'relative',
  transition: 'color 150ms ease-out',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: 1,

    background:
      'linear-gradient(180deg, rgb(0 0 0) 0%, rgb(0 0 0) 0%, rgb(0 0 0 / 0.1) 0%)'
  },

  // Apply styles to elements that appear before the active element
  '&:not(.toc--active):not(.toc--active ~ &)': {
    opacity: 0.75,

    '&::before': {
      background:
        'linear-gradient(180deg, rgb(0 0 0 / 0.3) 0%, rgb(0 0 0 / 0.3) 100%, rgb(0 0 0 / 0.1) 100%)'
    }
  },

  '&.toc--active': {
    opacity: 1,

    '&::before': {
      background:
        'linear-gradient(180deg, rgb(0 0 0) 0%, rgb(0 0 0) 100%, rgb(0 0 0 / 0.1) 100%)'
    }
  }
})

const NavLink = ({ children, className, ...props }) => {
  return (
    <StyledNavLink
      as="a"
      py={1}
      pl={3}
      className={cn('toc anchor', className)}
      {...props}
    >
      {children}
    </StyledNavLink>
  )
}

const TOCNav = styled('nav').withConfig({ shouldForwardProp })(
  {
    alignSelf: 'start',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  props => props.css,
  space,
  layout,
  typography,
  color,
  flexbox,
  position
)

const Container = styled('nav').withConfig({ shouldForwardProp })(
  {
    alignSelf: 'start',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 8,
    top: '2rem',
    position: 'relative'
  },
  props => props.css,
  space,
  layout,
  typography,
  color,
  flexbox,
  position
)

const TOCScrollArea = styled('div')({
  width: '100%',
  maxHeight: 'calc(100vh - 4rem)',
  overflowY: 'auto'
})

const VISIBLE_CLASSNAME = 'toc__scrolltop--visible'

const ScrollTopButton = styled('button').withConfig({ shouldForwardProp })(
  {
    unset: 'all',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,

    color: '#2E3135',
    backgroundColor: '#EDEEF0',
    cursor: 'pointer',

    fontSize: 14,
    fontWeight: 400,
    textDecoration: 'none',

    border: 0,
    borderRadius: 8,

    transition: 'all 80ms ease-out',

    opacity: 0,
    visibility: 'hidden',

    [`&.${VISIBLE_CLASSNAME}`]: {
      opacity: 1,
      visibility: 'visible'
    },

    '&:hover': {
      backgroundColor: '#B0B4BA'
    },

    '&:disabled': {
      pointerEvents: 'none',
      opacity: 0.5
    }
  },
  space,
  layout,
  color,
  flexbox,
  position
)

const TOCScrollTop = ({ ...props }) => {
  const buttonRef = useRef(null)

  const onClick = useCallback(() => {
    const scrollParent = getScrollParent(buttonRef.current)
    if (!scrollParent) {
      return
    }

    scrollParent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }, [])

  useLayoutEffect(() => {
    const scrollParent = getScrollParent(buttonRef.current)
    if (!scrollParent || !buttonRef.current) {
      return
    }

    const trigger = ScrollTrigger.create({
      scroller: scrollParent,
      start: '200px top',
      end: 'bottom bottom',
      onUpdate: ({ progress }) => {
        if (!buttonRef.current) {
          return
        }

        if (progress > 0) {
          buttonRef.current.classList.add(VISIBLE_CLASSNAME)
        } else {
          buttonRef.current.classList.remove(VISIBLE_CLASSNAME)
        }
      }
    })

    return () => {
      trigger.kill()
    }
  }, [])

  return (
    <ScrollTopButton
      onClick={onClick}
      type="button"
      pr={3}
      pl={2}
      py={2}
      ref={buttonRef}
      {...props}
    >
      <ChevronUp color="#2E3135" size={16} />
      <span>Back to top</span>
    </ScrollTopButton>
  )
}

const TOC = ({ tableOfContents, css, ...props }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const allLinks = Array.from(document.querySelectorAll('#tocnav a'))
    const allSections = Array.from(document.querySelectorAll('article h2'))

    if (!allSections.length || !allLinks.length) {
      return
    }

    const scrollParent = getScrollParent(containerRef.current)
    if (!scrollParent) {
      return
    }

    let ticking = false

    const setActiveSection = section => {
      if (!section?.id) {
        return
      }

      const activeLink = allLinks.find(link =>
        link.getAttribute('href')?.endsWith(`#${section.id}`)
      )

      if (!activeLink || activeLink.classList.contains('toc--active')) {
        return
      }

      allLinks.forEach(link => link.classList.remove('toc--active'))
      activeLink.classList.add('toc--active')
    }

    const updateActiveSection = () => {
      ticking = false

      const scrollRect = scrollParent.getBoundingClientRect()
      const offset = 80
      let activeSection = allSections[0]

      for (const section of allSections) {
        const top = section.getBoundingClientRect().top - scrollRect.top
        if (top <= offset) {
          activeSection = section
        }
      }

      setActiveSection(activeSection)
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(updateActiveSection)
      }
    }

    scrollParent.addEventListener('scroll', onScroll, { passive: true })
    updateActiveSection()

    return () => {
      scrollParent.removeEventListener('scroll', onScroll)
    }
  }, [tableOfContents])

  return (
    <Container
      as="aside"
      ref={containerRef}
      position={['static', 'sticky']}
      css={{
        ...css,
        'a.anchor': {
          textDecoration: 'none',
          transition:
            'text-decoration 0.175s ease-out, opacity 0.175s ease-out',
          '&:hover': {
            textDecoration: 'underline',
            opacity: 0.75
          }
        }
      }}
      {...props}
    >
      <Heading as="h4" fontSize={2} m={0} mt={1} fontWeight={600}>
        On this page
      </Heading>

      <TOCScrollArea>
        <TOCNav id="tocnav" py={1}>
          {tableOfContents.items
            .filter(item => item.title)
            .map((item, i) => {
              return (
                <NavLink
                  href={item.url}
                  key={item.url}
                  className={i === 0 ? 'toc--active' : ''}
                >
                  {item.title}
                </NavLink>
              )
            })}
        </TOCNav>
      </TOCScrollArea>

      <Hide xs s>
        <TOCScrollTop mt={3} />
      </Hide>
    </Container>
  )
}

const shouldShowTOC = toc =>
  toc &&
  toc.items &&
  toc.items.filter(({ title }) => title && title.length).length > 1

const TOCContainer = props => {
  const { tableOfContents } = props
  if (!shouldShowTOC(tableOfContents)) {
    return null
  }

  return <TOC {...props} />
}

export default TOCContainer
